<template>
  <div class="drawer-wrap">

    <!-- Logo Block -->
    <div class="sidebar-logo tw-hidden lg:tw-block">
      <router-link :to="{ name: 'AgentDashboard' }" @click.native="trackLogoClick">
        <img src="/pacis-logo.png" alt="Pacis Logo" class="logo-img" />
      </router-link>
    </div>

    <!-- Nav Items -->
    <nav class="sidebar-nav">
      <template v-for="item in items">

        <!-- Group (with children) -->
        <div
          v-if="item.children && !isAgentNotFound"
          :key="`${item.text}-group`"
          class="nav-group"
          :class="{ 'nav-group--open': openGroups.includes(item.text) }"
        >
          <button
            type="button"
            class="nav-item nav-group-trigger"
            :class="{ 'nav-item--active': isGroupActive(item) }"
            :data-agent-tour="tourSlug(item)"
            @click="onGroupToggle(item.text)"
          >
            <v-icon class="nav-icon" size="18">{{ item.icon }}</v-icon>
            <span class="nav-label">{{ item.text }}</span>
            <span
              v-if="groupCount(item) && !openGroups.includes(item.text) && !isGroupActive(item)"
              class="nav-count"
            >{{ groupCount(item) }}</span>
            <v-icon class="nav-chevron" size="16">
              {{ openGroups.includes(item.text) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </button>
          <div class="nav-children" v-show="openGroups.includes(item.text) || isGroupActive(item)">
            <router-link
              v-for="child in item.children"
              :key="child.text"
              :to="child.link"
              class="nav-child-item"
              active-class="nav-child-item--active"
              @click.native="trackNavItemClick(item.text, child.text, getRouteName(child.link))"
            >
              <span class="nav-child-dot"></span>
              {{ child.text }}
              <span
                v-if="countFor(child)"
                class="nav-count"
                :aria-label="`${renewalCount} up for renewal`"
              >{{ countFor(child) }}</span>
            </router-link>
          </div>
        </div>

        <!-- Single item -->
        <router-link
          v-else-if="!isAgentNotFound || item.text === 'Dashboard'"
          :key="`${item.text}-item`"
          :to="item.link"
          :data-agent-tour="tourSlug(item)"
          class="nav-item"
          active-class="nav-item--active"
          exact
          @click.native="trackNavItemClick('main', item.text, getRouteName(item.link))"
        >
          <v-icon class="nav-icon" size="18">{{ item.icon }}</v-icon>
          <span class="nav-label">{{ item.text }}</span>
        </router-link>

      </template>
    </nav>

    <!-- Bottom: Agent badge + Tour -->
    <div class="sidebar-footer">
      <button
        type="button"
        class="tour-trigger"
        @click="startTour"
        title="Take a tour"
      >
        <v-icon size="14" color="rgba(255,255,255,0.5)">mdi-help-circle-outline</v-icon>
        <span>Take a tour</span>
      </button>
      <div class="agent-badge">
        <v-icon size="14" color="rgba(255,255,255,0.5)">mdi-shield-account-outline</v-icon>
        <span>Agent Portal</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import { formatBadgeCount } from '@/utils/renewalReminders';

export default {
  name: 'AgentDrawer',
  computed: {
    ...mapGetters('auth', ['isAgentNotFound']),
    ...mapGetters('renewalReminders', { renewalCount: 'count' }),
  },
  data() {
    return {
      openGroups: [],
      items: [
        { icon: 'mdi-view-dashboard-outline', text: 'Dashboard', link: { name: 'AgentDashboard' } },
        {
          icon: 'mdi-file-document-edit-outline',
          text: 'Underwriting',
          children: [
            { text: 'Conversions', link: { name: 'AgentConversions' } },
            { text: 'Activity', link: { name: 'AgentQuotes' } },
            { text: 'Digital Certificates', link: { name: 'AgentCertificates' } },
            { text: 'Renewals', link: { name: 'AgentRenewals' }, countKey: 'renewals' },
            { text: 'Purchase Cover', link: { name: 'AgentProductSelect' } },
          ],
        },
        {
          icon: 'mdi-shield-alert-outline',
          text: 'Claims',
          children: [
            { text: 'View Client Claims', link: { name: 'AgentClientClaims' } },
          ],
        },
        {
          icon: 'mdi-currency-usd',
          text: 'Finance',
          children: [
            { text: 'Commissions', link: { name: 'AgentCommissions' } },
            { text: 'Transactions', link: { name: 'AgentTransactions' } },
          ],
        },
        // { icon: 'mdi-chart-bar-stacked', text: 'Reports', link: { name: 'AgentReports' } },
        // { icon: 'mdi-cog-outline', text: 'Settings', link: { name: 'AgentSettings' } },
      ],
    };
  },
  mounted() {
    // Auto-open any group that contains the active route
    this.items.filter((i) => i.children).forEach((item) => {
      if (this.isGroupActive(item)) {
        this.openGroups.push(item.text);
      }
    });
    this.$eventsHub.$on('agent-tour-open-group', this.handleTourOpenGroup);
  },
  beforeDestroy() {
    this.$eventsHub.$off('agent-tour-open-group', this.handleTourOpenGroup);
  },
  methods: {
    tourSlug(item) {
      return (item?.text || '').toLowerCase().replace(/\s+/g, '-');
    },
    getRouteName(link) {
      return link && link.name ? link.name : 'unknown';
    },
    handleTourOpenGroup(name) {
      if (!this.openGroups.includes(name)) this.toggleGroup(name);
    },
    startTour() {
      trackAgentInteraction(this, 'start_agent_tour');
      this.$nextTick(() => {
        const tour = this.$tours?.agentDrawerTour;
        if (tour) {
          tour.start();
        } else if (this.$toast && this.$toast.info) {
          this.$toast.info('Tour could not start. Please refresh and try again.', 'Tour');
        }
      });
    },
    countFor(child) {
      return child.countKey === 'renewals' ? formatBadgeCount(this.renewalCount) : '';
    },
    groupCount(item) {
      const child = (item.children || []).find((c) => c.countKey);
      return child ? this.countFor(child) : '';
    },
    isGroupActive(item) {
      if (!item.children) return false;
      const childRouteNames = item.children.map((c) => c.link?.name).filter(Boolean);
      if (childRouteNames.includes(this.$route.name)) return true;
      if (this.$route.name === 'AgentClientClaimDetails') return item.text === 'Claims';
      if (this.$route.name === 'AgentConversionDetails') return item.text === 'Underwriting';
      if (this.$route.name === 'AgentTransactionDetails') return item.text === 'Finance';
      return false;
    },
    toggleGroup(text) {
      const idx = this.openGroups.indexOf(text);
      if (idx >= 0) {
        this.openGroups.splice(idx, 1);
      } else {
        this.openGroups.push(text);
      }
    },
    onGroupToggle(text) {
      const willOpen = !this.openGroups.includes(text);
      this.toggleGroup(text);
      trackAgentInteraction(this, 'drawer_group_toggle', {
        group_name: text,
        group_state: willOpen ? 'open' : 'close',
      });
    },
    trackNavItemClick(groupName, itemName, destination) {
      trackAgentInteraction(this, 'drawer_nav_click', {
        nav_group: groupName,
        nav_item: itemName,
        destination: destination || 'unknown',
      });
    },
    trackLogoClick() {
      trackAgentInteraction(this, 'drawer_logo_click', {
        destination: 'AgentDashboard',
      });
    },
  },
};
</script>

<style scoped>
/* ── Wrapper ── */
.drawer-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  min-height: 100vh;
}

/* ── Logo ── */
.sidebar-logo {
  padding: 16px 20px 14px;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  margin-bottom: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.sidebar-logo a {
  display: block;
  line-height: 0;
}

.logo-img {
  width: 110px;
  height: auto;
  display: block;
  filter: brightness(0) invert(1);
}

/* ── Nav ── */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sidebar-nav::-webkit-scrollbar {
  width: 0;
}

/* ── Nav Item (base) ── */
.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.875rem;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
  cursor: pointer;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.07);
  color: white;
}

/* ── Active nav item ── */
.nav-item--active {
  background: #00AEEF !important;
  color: white !important;
  font-weight: 700;
}

.nav-item--active .nav-icon {
  color: white !important;
}

.nav-icon {
  color: rgba(255, 255, 255, 0.55);
  flex-shrink: 0;
  transition: color 0.15s;
}

.nav-item:hover .nav-icon {
  color: white;
}

.nav-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Chevron ── */
.nav-chevron {
  color: rgba(255, 255, 255, 0.4) !important;
  flex-shrink: 0;
  transition: transform 0.2s;
}

.nav-group--open .nav-group-trigger .nav-chevron {
  transform: rotate(180deg);
}

/* ── Group trigger ── */
.nav-group-trigger {
  width: 100%;
}

/* ── Children ── */
.nav-children {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 2px;
  padding-left: 14px;
}

.nav-child-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px 8px 14px;
  border-radius: 6px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-child-item:hover {
  background: rgba(255, 255, 255, 0.06);
  color: white;
}

.nav-child-item--active {
  color: #00AEEF !important;
  background: rgba(0, 174, 239, 0.1) !important;
  font-weight: 700;
  border-left-color: #00AEEF;
}

.nav-child-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
  opacity: 0.6;
}

.nav-child-item--active .nav-child-dot {
  opacity: 1;
}

.nav-count {
  margin-left: auto;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #25D366;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 20px;
  text-align: center;
  flex-shrink: 0;
}

/* ── Footer ── */
.sidebar-footer {
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.agent-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.tour-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 16px;
  margin-bottom: 8px;
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.tour-trigger:hover {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.4);
}
</style>
